import * as React from "react";

import clsxMerge from "@v1/components/designSystem/shadcn/utils/cn-merge";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

type CalendarProps = React.ComponentProps<typeof DayPicker>;
export default function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={clsxMerge("p-3", className)}
      classNames={{
        months:
          "flex flex-col sm:flex-row space-y-4 sm:gap-4 sm:space-y-0 relative",
        month: "space-y-4",
        month_caption: "flex justify-center pt-1.5 items-center",
        caption_label: "text-default text-base font-semibold",
        nav: "space-x-1 flex justify-between absolute w-full z-5",
        button_previous:
          "flex items-center justify-center h-8 w-8 border-0.5 border-neutral-200 rounded-sm bg-transparent p-0",
        button_next:
          "flex items-center justify-center h-8 w-8 border-0.5 border-neutral-200 rounded-sm bg-transparent p-0",
        table: "w-full border-collapse space-y-1",
        weekdays: "flex w-full",
        weekday:
          "text-placeholder rounded-md w-[42px] h-[24px] font-normal text-[14px]",
        week: "flex w-full mt-1",
        day: clsxMerge(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
        ),
        hidden: "invisible",
        range_start: "text-white",
        range_end: "text-white",
        ...classNames,
      }}
      modifiers={{
        saturday: [(date) => date.getDay() === 6],
        sunday: [(date) => date.getDay() === 0],
        currentDay: [
          (date) => {
            return (
              date.getDate() == new Date().getDate() &&
              date.getMonth() == new Date().getMonth()
            );
          },
        ],
        anyDay: [() => true],
      }}
      components={{
        DayButton({ day, modifiers, className, ...buttonProps }) {
          return (
            <div
              className={clsxMerge(
                className,
                (modifiers.range_start || modifiers.range_end) && "bg-sky-soft",
                modifiers.range_start && "rounded-s-[8px]",
                modifiers.range_end && "rounded-e-[8px]",
              )}
            >
              <button
                className={clsxMerge(
                  className,
                  "typ-body-small h-[42px] w-[42px] p-0 font-normal",
                  modifiers?.selected && "bg-primary rounded-lg text-white",
                  modifiers?.outside && "text-neutral-200 pointer-events-none",
                  modifiers?.disabled && "text-neutral-200 pointer-events-none",
                  modifiers?.hidden && "invisible",
                  modifiers.range_middle &&
                    "!bg-selectable-selected text-default hover:text-default rounded-none",
                  modifiers.outside &&
                    modifiers.selected &&
                    "bg-selectable-selected/40 text-muted opacity-50",
                  modifiers.saturday && "rounded-r-[8px]",
                  modifiers.sunday && "rounded-l-[8px]",
                  modifiers.currentDay && "font-bold",
                  modifiers.anyDay &&
                    !modifiers.disabled &&
                    !modifiers.selected &&
                    !modifiers.range_middle &&
                    "hover:bg-selectable-selected-hover rounded-lg",
                )}
                {...buttonProps}
                aria-selected={
                  modifiers.selected || buttonProps["aria-selected"]
                }
                aria-disabled={
                  modifiers.disabled || buttonProps["aria-disabled"]
                }
                aria-hidden={modifiers.hidden || buttonProps["aria-hidden"]}
                ref={React.useRef<HTMLButtonElement>(null)}
              >
                {day.date.getDate()}
              </button>
            </div>
          );
        },
        Chevron({ orientation, disabled, className }) {
          const Component = orientation === "left" ? ChevronLeft : ChevronRight;
          return (
            <Component
              className={clsxMerge("h-4 w-4", className)}
              aria-disabled={disabled}
            />
          );
        },
      }}
      {...props}
    />
  );
}
